import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faShieldAlt, faStore, faPhone, faMapMarkerAlt, faTachometerAlt, faCalendarAlt, faHashtag,
  faWrench, faCogs, faListUl, faStar, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import Carousel, { Modal as Lightbox, ModalGateway } from 'react-images';

import EvaluationView from '../desktop/layouts/Client/pages/maintances/EvaluationView';
import { handleChange, closeModal } from '../components/evaluation/reduxEvaluation/evaluationAction';
import { loadRecordDetail, toEvaluationValues } from './garageData';
import Plate from './Plate';
import { formatCnpj, formatKm, formatLongDate, formatMoney, formatPhone, recordTitle } from './format';

const GROUPS = [
  { kind: 'service', title: 'Cuidados registrados', icon: faWrench },
  { kind: 'product', title: 'Peças e produtos', icon: faCogs },
  { kind: 'item', title: 'Itens do atendimento', icon: faListUl },
];

function Items({ items }) {
  return GROUPS.map((group) => {
    const list = items.filter((item) => item.kind === group.kind);
    if (!list.length) return null;
    return (
      <section key={group.kind} className='rs-block'>
        <h3><FontAwesomeIcon icon={group.icon} aria-hidden='true' />{group.title}</h3>
        <ul className='rs-items'>
          {list.map((item, index) => (
            <li key={`${item.description}-${index}`} className='rs-item'>
              <div className='rs-item-main'>
                <strong>{item.description}</strong>
                <span>
                  {item.quantity !== null && `${item.quantity} × ${formatMoney(item.unitValue)}`}
                  {item.discount ? ` · desconto ${item.discount}%` : ''}
                </span>
                {(item.warranty || item.review) && (
                  <span className='rs-item-extra'>
                    {item.warranty && `Garantia: ${item.warranty}`}
                    {item.warranty && item.review && ' · '}
                    {item.review && `Próxima revisão: ${item.review}`}
                  </span>
                )}
              </div>
              <span className='rs-item-total'>{formatMoney(item.total)}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  });
}

// Detalhe de um atendimento: folha que sobe no celular, gaveta lateral no desktop.
export default function RecordSheet({ record, onClose }) {
  const dispatch = useDispatch();
  const { modalEvaluation } = useSelector((state) => state.evaluationReducer);
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let active = true;
    loadRecordDetail(record)
      .then((data) => { if (active) setDetail(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [record]);

  useEffect(() => {
    closeRef.current && closeRef.current.focus();
    const onKey = (event) => { if (event.key === 'Escape' && !modalEvaluation) onClose(); };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, modalEvaluation]);

  const address = detail ? detail.address : record.company.address;
  const phone = formatPhone(record.company.phone);

  return createPortal(
    <div className='rs-overlay' onClick={onClose}>
      <div
        className='rs-sheet'
        role='dialog'
        aria-modal='true'
        aria-labelledby='rs-title'
        onClick={(event) => event.stopPropagation()}
      >
        <header className='rs-header'>
          <span className='rs-grabber' aria-hidden='true' />
          <div>
            <span className='rs-date'>{formatLongDate(record.date)}</span>
            <h2 id='rs-title'>{recordTitle(record)}</h2>
          </div>
          <button type='button' ref={closeRef} className='rs-close' onClick={onClose} aria-label='Fechar detalhes'>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className='rs-body'>
          <p className='rs-seal'>
            <FontAwesomeIcon icon={faShieldAlt} aria-hidden='true' />
            Registro enviado pela empresa responsável pelo atendimento.
          </p>

          <section className='rs-block'>
            <h3>Resumo</h3>
            <dl className='rs-facts'>
              <div><dt><FontAwesomeIcon icon={faCalendarAlt} aria-hidden='true' />Data</dt><dd>{formatLongDate(record.date)}</dd></div>
              {record.km ? <div><dt><FontAwesomeIcon icon={faTachometerAlt} aria-hidden='true' />Quilometragem</dt><dd>{formatKm(record.km)}</dd></div> : null}
              <div><dt><FontAwesomeIcon icon={faHashtag} aria-hidden='true' />Número</dt><dd>{record.code}</dd></div>
              <div><dt>Veículo</dt><dd className='rs-vehicle'><Plate value={record.plate} size='sm' />{record.vehicleLabel}</dd></div>
            </dl>
          </section>

          <section className='rs-block rs-company'>
            <h3><FontAwesomeIcon icon={faStore} aria-hidden='true' />Empresa responsável</h3>
            <div className='rs-company-card'>
              <span className='rs-company-avatar' aria-hidden='true'>{record.company.name.charAt(0)}</span>
              <div>
                <strong>{record.company.name}</strong>
                {record.company.cnpj && <span>CNPJ {formatCnpj(record.company.cnpj)}</span>}
                <span className='rs-connected'><FontAwesomeIcon icon={faCheckCircle} aria-hidden='true' />Empresa conectada ao Sobre Rodas</span>
              </div>
            </div>
            <ul className='rs-contact'>
              {phone && <li><FontAwesomeIcon icon={faPhone} aria-hidden='true' /><a href={`tel:+55${record.company.phone}`}>{phone}</a></li>}
              {address && <li><FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden='true' /><span>{address}{record.company.city ? ` · ${record.company.city}` : ''}</span></li>}
            </ul>
          </section>

          {!detail && !failed && <div className='rs-loading' aria-busy='true'><div className='gs-skeleton' /></div>}
          {failed && <p className='gs-notice'>Não foi possível carregar os itens deste atendimento agora.</p>}
          {detail && detail.items.length === 0 && <p className='gs-notice'>A empresa não enviou os itens deste atendimento.</p>}
          {detail && <Items items={detail.items} />}

          {record.total !== null && (
            <section className='rs-block'>
              <h3>Valores</h3>
              <dl className='rs-totals'>
                {record.subtotal !== null && <div><dt>Subtotal</dt><dd>{formatMoney(record.subtotal)}</dd></div>}
                {record.discount ? <div><dt>Desconto</dt><dd>− {formatMoney(record.discount)}</dd></div> : null}
                <div className='rs-total'><dt>Total</dt><dd>{formatMoney(record.total)}</dd></div>
              </dl>
            </section>
          )}

          {record.photos.length > 0 && (
            <section className='rs-block'>
              <h3>Fotos do atendimento</h3>
              <ul className='rs-photos'>
                {record.photos.map((photo, index) => (
                  <li key={photo.link}>
                    <button type='button' onClick={() => setPhotoIndex(index)} aria-label={`Ampliar foto ${index + 1}`}>
                      <img src={photo.link} alt={photo.name || ''} loading='lazy' />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {(record.canEvaluate || record.evaluated) && (
          <footer className='rs-footer'>
            <button type='button' className={`gs-btn ${record.canEvaluate ? 'gs-btn-primary' : 'gs-btn-secondary'}`} onClick={() => dispatch(handleChange(true, 'modalEvaluation'))}>
              <FontAwesomeIcon icon={faStar} aria-hidden='true' />
              {record.canEvaluate ? 'Avaliar este atendimento' : 'Ver minha avaliação'}
            </button>
          </footer>
        )}
      </div>

      <ModalGateway>
        {photoIndex !== null ? (
          <Lightbox onClose={() => setPhotoIndex(null)}>
            <Carousel views={record.photos.map((photo) => ({ src: photo.link }))} currentIndex={photoIndex} />
          </Lightbox>
        ) : null}
      </ModalGateway>

      {modalEvaluation && (
        <div onClick={(event) => event.stopPropagation()}>
          <EvaluationView
            modalEvaluation={modalEvaluation}
            values={toEvaluationValues(record)}
            close={() => dispatch(closeModal())}
            dataManutencao={record.date ? record.date.toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : ''}
            infoModalEvaluation={{ Company: record.company.name }}
          />
        </div>
      )}
    </div>,
    document.body,
  );
}
